import { services } from "./servicesData";
import { useRef, useEffect, useState } from "react";

// Intersection Observer for reveal-on-visible
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function ServicePage({ serviceSlug }) {
  // Use the serviceSlug prop instead of useParams.
  const slug = serviceSlug;
  const service = services.find((s) => s.slug === slug);

  // Animation logic
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isVisible = useOnScreen(sectionRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Animation styles
  const animStyle = (delay = 0, y = 32, duration = 1200) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0)" : `translateY(${y}px)`,
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  if (!service) {
    return <div className="text-center py-20 font-PTSerif-Regular">Service Not Found</div>;
  }

  return (
    <section
      className="pb-24 pt-10 md:pb-24 md:pt-40 bg-white font-PTSerif-Regular"
      ref={sectionRef}
      style={{ position: "relative", zIndex: 1 }}
    >
      <div className="max-w-6xl mx-auto px-6 font-PTSerif-Regular">

        {/* Hero */}
        <div className="mb-6 md:mb-12" style={animStyle(0, 32, 1000)}>
          <img
            src={service.hero}
            alt={service.title}
            className="w-full md:w-fit  md:h-[420px] object-contain rounded-lg mx-auto font-PTSerif-Regular"
          />
        </div>

        {/* Title */}
        <h1
          className="text-4xl font-bold text-gray-900 mb-6 font-PTSerif-Regular"
          style={animStyle(120, 22, 900)}
        >
          {service.title}
        </h1>

        <p
          className="text-gray-600 mb-10 font-PTSerif-Regular"
          style={animStyle(220, 16, 900)}
        >
          {service.description}
        </p>

        {/* Details */}
        <div className="mb-14 font-PTSerif-Regular" style={animStyle(320, 20, 800)}>
          <h2 className="text-2xl font-semibold mb-6 font-PTSerif-Regular">
            Service Details
          </h2>

          <ul className="grid md:grid-cols-2 gap-4 font-PTSerif-Regular">
            {service.details.map((item, index) => (
              <li
                key={index}
                className="bg-gray-100 p-4 rounded font-PTSerif-Regular"
                style={animStyle(400 + index * 60, 22)}
              >
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Features */}
        <div
          className="bg-blue-50 p-10 rounded-lg font-PTSerif-Regular"
          style={animStyle(560, 24, 900)}
        >
          <h2 className="text-2xl font-semibold mb-6 font-PTSerif-Regular">
            Key Benefits
          </h2>
          <div className="grid md:grid-cols-4 gap-6 font-PTSerif-Regular">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded shadow text-center font-PTSerif-Regular"
                style={animStyle(680 + index * 60, 22)}
              >
                <p className="text-gray-700 font-medium font-PTSerif-Regular">
                  {feature}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}